/**
 * Typed API client for the ATLAS Worker.
 *
 * All requests include the CF-Access-JWT-Assertion header automatically
 * (Cloudflare Pages injects it when behind CF Access).
 *
 * Returns the typed data payload, or throws an ApiError.
 */

import type {
  ApiResponse,
  OverviewStats,
  DomainView,
  ClientView,
} from "@atlas/shared/types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";

/** Read the CF_Authorization cookie set by Cloudflare Access. */
function getCfAccessToken(): string | null {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("CF_Authorization="));
  return match ? match.slice("CF_Authorization=".length) : null;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const cfToken = getCfAccessToken();
  const res = await fetch(`${BASE}${path}`, {
    cache: "no-store",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(cfToken ? { "CF-Access-JWT-Assertion": cfToken } : {}),
      ...init?.headers,
    },
  });

  const json = (await res.json()) as ApiResponse<T>;

  if (!res.ok || json.error) {
    throw new ApiError(res.status, json.error ?? `HTTP ${res.status}`);
  }

  if (json.data === null) {
    throw new ApiError(res.status, "API returned null data");
  }

  return json.data;
}

// ─── Overview ─────────────────────────────────────────────────────────────────

export async function fetchOverview(): Promise<OverviewStats> {
  return apiFetch<OverviewStats>("/overview");
}

// ─── Domains ──────────────────────────────────────────────────────────────────

export async function fetchDomains(): Promise<DomainView[]> {
  return apiFetch<DomainView[]>("/domains");
}

export async function fetchDomain(id: number): Promise<DomainView & { snapshotHistory: unknown[] }> {
  return apiFetch(`/domains/${id}`);
}

export async function addDomain(body: {
  clientId: number;
  hostname: string;
  port?: number;
  tags?: string[];
  isInternal?: boolean;
}): Promise<{ id: number }> {
  return apiFetch("/domains", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteDomain(id: number): Promise<{ id: number; active: false }> {
  return apiFetch(`/domains/${id}`, { method: "DELETE" });
}

// ─── Clients ──────────────────────────────────────────────────────────────────

export async function fetchClients(): Promise<ClientView[]> {
  return apiFetch<ClientView[]>("/clients");
}

export async function addClient(body: {
  name: string;
  slug: string;
  slackWebhook?: string;
}): Promise<{ id: number }> {
  return apiFetch("/clients", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function fetchClient(id: number): Promise<ClientView & { domains: unknown[] }> {
  return apiFetch(`/clients/${id}`);
}

export async function updateClient(id: number, body: { name: string; slackWebhook?: string }): Promise<{ id: number }> {
  return apiFetch(`/clients/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function deleteClient(id: number): Promise<{ id: number; deleted: true }> {
  return apiFetch(`/clients/${id}`, { method: "DELETE" });
}

export async function rotateAgentKey(id: number): Promise<{ apiKey: string; warning: string }> {
  return apiFetch(`/clients/${id}/rotate-key`, { method: "POST" });
}

// ─── Scan ─────────────────────────────────────────────────────────────────────

export async function triggerScan(hostname: string, port = 443): Promise<unknown> {
  return apiFetch("/scan", {
    method: "POST",
    body: JSON.stringify({ hostname, port }),
  });
}

export async function triggerDomainScan(domainId: number): Promise<unknown> {
  return apiFetch(`/scan/domain/${domainId}`, { method: "POST" });
}

// ─── Alerts ───────────────────────────────────────────────────────────────────

export async function fetchAlerts(params?: {
  domainId?: number;
  page?: number;
  limit?: number;
}): Promise<unknown[]> {
  const qs = new URLSearchParams();
  if (params?.domainId) qs.set("domainId", String(params.domainId));
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  return apiFetch(`/alerts${qs.size ? `?${qs}` : ""}`);
}

export async function acknowledgeAlert(id: number): Promise<{ id: number; acknowledged: true }> {
  return apiFetch(`/alerts/${id}/acknowledge`, { method: "POST" });
}
